import clsx from 'clsx'
import { type MouseEvent, type Ref } from 'react'

import { useCtaTracking } from '@lib/hooks/use-cta-tracking'

import { ComponentCtaButtonType } from '@constants/enums/cta'
import { type IconSprites } from '@constants/enums/sprites'

import { Button } from '@components/ui-core/button/button'
import { Grid } from '@components/ui-core/grid/grid'
import { Icon } from '@components/ui-core/icon/icon'
import { Text } from '@components/ui-core/text/text'

import { type TypeTabbedSectionData } from '@custom/pages/homepage/types'

import { HomepageText } from '../../text/homepage-text'

import styles from './tabbed-section-header.module.scss'

export default function TabbedSectionHeader({
  ref,
  data,
  selectedTabId,
  onNavItemClick,
}: {
  ref?: Ref<HTMLDivElement>
  data?: TypeTabbedSectionData
  selectedTabId?: string
  onNavItemClick?: (id: string) => void
}) {
  const { trackAndNavigate, isLoading } = useCtaTracking({ placement: 'Section' })
  const hasContent = data?.content?.length && data?.content?.length > 0

  const handleClick = (e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>) => {
    if (!data?.link?.fields?.targetUrl) {
      return
    }

    trackAndNavigate(
      ComponentCtaButtonType.Signup,
      data?.link?.fields?.title ?? '',
      'secondary',
      data?.link?.fields?.targetUrl,
      e,
    )
  }

  return (
    <header ref={ref} className={styles.header}>
      <div className={styles.headerContent}>
        <HomepageText as="h2" variant="headingMedium">
          {data?.heading}
        </HomepageText>
        <Button
          category="secondary"
          href={data?.link?.fields?.targetUrl}
          onClick={handleClick}
          isLoading={isLoading}
        >
          {data?.link?.fields?.title}
        </Button>
      </div>
      <Grid className={styles.nav}>
        {hasContent &&
          data?.content?.map(section => (
            <Button
              category="unstyled"
              onClick={() => onNavItemClick?.(section?.id)}
              key={`nav-item-${section?.id}`}
              className={clsx(styles.navItem, selectedTabId === section?.id ? styles.active : '')}
            >
              <Icon variant={section?.icon as IconSprites} />
              <Text
                className={styles.navItemText}
                as="h2"
                variant="headerSmall"
                fontWeight="regular"
              >
                {section?.title}
              </Text>
            </Button>
          ))}
      </Grid>
    </header>
  )
}
